/**
 * "+N more" popup for the product media grid (snippets/product-media-gallery-content.liquid).
 *
 * The last visible grid tile opens a dialog with a slider of every media item. Slides are a
 * scroll-snap track, so swiping works natively; arrows, thumbnails and the keyboard move it too.
 */
class MediaGalleryPopup extends HTMLElement {
  index = 0;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.track = this.querySelector('[data-popup-track]');
    if (!this.dialog || !this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('[data-popup-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-popup-thumb]'));
    this.thumbsContainer = this.querySelector('[data-popup-thumbs]');
    this.counter = this.querySelector('[data-popup-current]');
    this.prevButton = this.querySelector('[data-popup-prev]');
    this.nextButton = this.querySelector('[data-popup-next]');

    this.addEventListener('click', this.#handleClick);
    this.dialog.addEventListener('keydown', this.#handleKeydown);
    this.dialog.addEventListener('close', this.#handleClose);
    this.track.addEventListener('scroll', this.#handleScroll, { passive: true });

    // Opener buttons live in the grid, outside this element
    this.openers = document.querySelectorAll(`[data-media-popup-open="${this.id}"]`);
    this.openers.forEach((button) => button.addEventListener('click', this.#handleOpenerClick));

    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.dialog?.removeEventListener('keydown', this.#handleKeydown);
    this.dialog?.removeEventListener('close', this.#handleClose);
    this.track?.removeEventListener('scroll', this.#handleScroll);
    this.openers?.forEach((button) => button.removeEventListener('click', this.#handleOpenerClick));
  }

  /** @param {number} [startIndex] */
  open(startIndex = 0) {
    if (!this.dialog || this.dialog.open) return;

    this.dialog.classList.remove('media-popup--closing');
    this.dialog.showModal();
    // The track only has a size once the dialog is shown
    this.goTo(startIndex, false);
    this.track?.focus({ preventScroll: true });
  }

  close() {
    const { dialog } = this;
    if (!dialog?.open) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return dialog.close();

    // Let the closing animation play before the dialog leaves the top layer
    dialog.classList.add('media-popup--closing');
    dialog.addEventListener('animationend', () => dialog.close(), { once: true });
  }

  /**
   * @param {number} index
   * @param {boolean} [smooth]
   */
  goTo(index, smooth = true) {
    if (!this.track || !this.slides?.length) return;

    const count = this.slides.length;
    const target = Math.max(0, Math.min(count - 1, index));
    const slide = this.slides[target];
    if (!slide) return;

    this.track.scrollTo({ left: slide.offsetLeft, behavior: smooth ? 'smooth' : 'instant' });
    this.#setIndex(target);
  }

  /** @param {number} index */
  #setIndex(index) {
    if (index === this.index && this.counter?.textContent === String(index + 1)) return;

    this.index = index;
    this.#pauseMedia();
    this.#update();
  }

  #update() {
    const count = this.slides?.length ?? 0;

    if (this.counter) this.counter.textContent = String(this.index + 1);
    if (this.prevButton) this.prevButton.disabled = this.index <= 0;
    if (this.nextButton) this.nextButton.disabled = this.index >= count - 1;

    this.thumbs?.forEach((thumb, i) => {
      if (i === this.index) {
        thumb.setAttribute('aria-current', 'true');
        this.#scrollThumbIntoView(thumb);
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }

  /** Scrolls only the thumbnail strip, so the dialog itself never jumps. */
  /** @param {HTMLElement} thumb */
  #scrollThumbIntoView(thumb) {
    const container = this.thumbsContainer;
    if (!container) return;

    const left = thumb.offsetLeft - container.clientWidth / 2 + thumb.clientWidth / 2;
    container.scrollTo({ left, behavior: 'smooth' });
  }

  /** Stops any playing video when its slide is left or the popup closes. */
  #pauseMedia() {
    this.track?.querySelectorAll('video').forEach((video) => video.pause());
    this.track?.querySelectorAll('iframe').forEach((iframe) => {
      const win = iframe.contentWindow;
      if (!win) return;
      win.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      win.postMessage('{"method":"pause"}', '*');
    });
  }

  #handleScroll = () => {
    if (!this.track) return;

    // Settle on whichever slide is closest to the start of the track
    const width = this.track.clientWidth || 1;
    const index = Math.round(Math.abs(this.track.scrollLeft) / width);
    this.#setIndex(Math.max(0, Math.min((this.slides?.length ?? 1) - 1, index)));
  };

  /** @param {MouseEvent} event */
  #handleOpenerClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.currentTarget);
    this.open(Number(button.dataset.mediaPopupStart) || 0);
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    // A click on the dialog itself is a click on the backdrop
    if (target === this.dialog || target.closest('[data-media-popup-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    if (target.closest('[data-popup-prev]')) return this.goTo(this.index - 1);
    if (target.closest('[data-popup-next]')) return this.goTo(this.index + 1);

    const thumb = target.closest('[data-popup-thumb]');
    if (thumb) this.goTo(Number(thumb.getAttribute('data-popup-thumb')));
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const rtl = document.dir === 'rtl';

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.index + (rtl ? -1 : 1));
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.index + (rtl ? 1 : -1));
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  };

  #handleClose = () => {
    this.dialog?.classList.remove('media-popup--closing');
    this.#pauseMedia();
  };
}

if (!customElements.get('media-gallery-popup')) {
  customElements.define('media-gallery-popup', MediaGalleryPopup);
}
